import { useMemo } from 'react';
import {
  FileWithValidation,
  FileStatistics,
  UseFileStatisticsReturn,
  TableMode,
  ProcessedFile,
  FileStatus,
} from '../types';

interface UseFileStatisticsProps {
  files: FileWithValidation[];
  tableMode?: TableMode;
  uploadQueueState?: {
    files: Array<{
      file: File;
      status: 'pending' | 'uploading' | 'completed' | 'error' | 'cancelled';
      progress?: number;
      error?: string;
    }>;
  };
}

export const useFileStatistics = ({
  files,
  tableMode = TableMode.DUPLICATE_CHECK,
  uploadQueueState,
}: UseFileStatisticsProps): UseFileStatisticsReturn => {
  // O(1) lookup map for upload queue status
  const uploadStatusMap = useMemo(() => {
    if (!uploadQueueState?.files?.length) return null;
    const map = new Map<
      string,
      (typeof uploadQueueState.files)[0]
    >();
    for (const qf of uploadQueueState.files) {
      map.set(
        `${qf.file.name}-${qf.file.size}-${qf.file.lastModified}`,
        qf
      );
    }
    return map;
  }, [uploadQueueState?.files]);

  // Process files with duplicate detection and status mapping
  const processedFiles = useMemo((): ProcessedFile[] => {
    const nameCount: Record<string, number> = {};

    // Count file names
    files.forEach(file => {
      const name = file.file.name;
      nameCount[name] = (nameCount[name] || 0) + 1;
    });

    // Process files with status
    return files.map((file): ProcessedFile => {
      const isDuplicate = nameCount[file.file.name] > 1;

      let processedStatus: FileStatus = 'ready-to-check';

      if (isDuplicate) {
        processedStatus = 'duplicated';
      } else {
        // Map validation status to file status
        switch (file.validationStatus.status) {
          case 'pending':
            processedStatus = 'ready-to-check';
            break;
          case 'server-valid':
            processedStatus = file.validationStatus.serverValidation?.exists
              ? 'exists'
              : 'allowed';
            break;
          case 'server-invalid':
          case 'invalid':
          case 'error':
            processedStatus = 'not-allowed';
            break;
          case 'validating':
          case 'server-validating':
            processedStatus = 'ready-to-check';
            break;
          default:
            processedStatus = 'ready-to-check';
        }

        // In upload progress mode, check upload queue status (O(1) via map)
        if (tableMode === TableMode.UPLOAD_PROGRESS && uploadStatusMap) {
          const key = `${file.file.name}-${file.file.size}-${file.file.lastModified}`;
          const queuedFile = uploadStatusMap.get(key);

          if (queuedFile) {
            switch (queuedFile.status) {
              case 'pending':
                processedStatus = 'pending';
                break;
              case 'uploading':
                processedStatus = 'uploading';
                break;
              case 'completed':
                processedStatus = 'uploaded';
                break;
              case 'error':
              case 'cancelled':
                processedStatus = 'failed';
                break;
            }
          } else if (processedStatus === 'not-allowed') {
            processedStatus = 'failed';
          } else if (
            processedStatus === 'allowed' ||
            processedStatus === 'exists'
          ) {
            processedStatus = 'pending';
          }
        }
      }

      return {
        ...file,
        processedStatus,
        duplicateGroup: isDuplicate ? file.file.name : undefined,
      };
    });
  }, [files, tableMode, uploadStatusMap]);

  // Calculate comprehensive statistics
  const statistics = useMemo((): FileStatistics => {
    const stats: FileStatistics = {
      total: processedFiles.length,
      duplicated: 0,
      readyToCheck: 0,
      serverValidating: 0,
      allowed: 0,
      notAllowed: 0,
      exists: 0,
      readyToUpload: 0,
      // Upload progress stats
      pending: 0,
      uploading: 0,
      uploaded: 0,
      failed: 0,
    };

    processedFiles.forEach(file => {
      const status = file.processedStatus;

      // Count by processed status
      switch (status) {
        case 'duplicated':
          stats.duplicated++;
          break;
        case 'ready-to-check':
          stats.readyToCheck++;
          break;
        case 'allowed':
          stats.allowed++;
          break;
        case 'not-allowed':
          stats.notAllowed++;
          break;
        case 'exists':
          stats.exists++;
          break;
        case 'pending':
          stats.pending++;
          break;
        case 'uploading':
          stats.uploading++;
          break;
        case 'uploaded':
          stats.uploaded++;
          break;
        case 'failed':
          stats.failed++;
          break;
      }

      // Count server validating files
      if (file.validationStatus.status === 'server-validating') {
        stats.serverValidating++;
      }

      // Count files ready for upload
      if (file.validationStatus.status === 'server-valid') {
        if (!file.validationStatus.serverValidation?.exists) {
          stats.readyToUpload++;
        } else if (file.uploadStrategy === 'overwrite') {
          stats.readyToUpload++;
        }
      }
    });

    return stats;
  }, [processedFiles]);

  // Get duplicate file names with stats
  const { duplicateFileNames, duplicateFileStats } = useMemo(() => {
    const nameCount: Record<string, number> = {};

    // Count all files by name first
    processedFiles.forEach(file => {
      const name = file.file.name;
      nameCount[name] = (nameCount[name] || 0) + 1;
    });

    // Get files that have duplicates (count > 1)
    const duplicateNames = Object.keys(nameCount).filter(
      name => nameCount[name] > 1
    );
    const duplicateStats = duplicateNames.reduce(
      (acc, name) => {
        acc[name] = nameCount[name];
        return acc;
      },
      {} as Record<string, number>
    );

    return {
      duplicateFileNames: duplicateNames,
      duplicateFileStats: duplicateStats || {},
    };
  }, [processedFiles]);

  // Calculate derived states
  const hasErrors = useMemo(() => {
    return processedFiles.some(
      f =>
        f.validationStatus.status === 'error' ||
        f.validationStatus.status === 'invalid' ||
        f.validationStatus.status === 'server-invalid'
    );
  }, [processedFiles]);

  const hasValidationResults = useMemo(() => {
    return processedFiles.some(
      f =>
        f.validationStatus.status === 'server-valid' ||
        f.validationStatus.status === 'server-invalid'
    );
  }, [processedFiles]);

  const hasDuplicates = useMemo(() => {
    return statistics.duplicated > 0;
  }, [statistics.duplicated]);

  // Get conflicting files (files that exist on server)
  const conflictingFiles = useMemo(() => {
    return processedFiles.filter(
      f =>
        f.validationStatus.status === 'server-valid' &&
        f.validationStatus.serverValidation?.exists
    );
  }, [processedFiles]);

  return {
    statistics,
    duplicateFileNames,
    duplicateFileStats,
    hasErrors,
    hasValidationResults,
    hasDuplicates,
    conflictingFiles,
  };
};
